/** @format */

import {addDays} from 'date-fns';

import {ComplexForecastPredicate} from 'features/forecast/types';
import {getPlanForDate} from 'features/mediaplan-progress/helpers/getPlanForDate';
import {selectActivePeriod} from 'features/mediaplan-progress/helpers/selectActivePeriod';
import {toIso} from 'features/mediaplan-progress/helpers/toIso';
import {toPlanPeriodInput} from 'features/mediaplan-progress/helpers/toPlanPeriodInput';
import {
  ComponentBag,
  DailySeries,
  IsoDate,
  MetricInput,
  Period,
  PlanPeriodInput,
  RatioSpec,
} from 'features/mediaplan-progress/types';

import {CHARTS_PREDICATES} from 'pages/Home/DigitalCast/constants/predicates';
import {WIDGETS_LIST} from 'pages/Home/DigitalCast/constants/widgets';
import getChartIdByKpiName from 'pages/Home/DigitalCast/helpers/getChartIdByKpiName';
import {ChartIds} from 'pages/Home/DigitalCast/models/chartIds';

import {CardBodyCompany} from '../types';

// id графика DigitalCast (ChartIds).
export type CardMetric = string;

interface Options {
  company: CardBodyCompany;
  metric: CardMetric;
  today: IsoDate;
  // Pre-resolved active plan/period: resolve it once per card (selectActiveMediaplan) and thread it
  // in, instead of re-deriving it for every metric. Omit to resolve internally.
  active?: ActiveMediaplan | null;
}

type GraphStat = CardBodyCompany['graphStats'][number];
type MediaplanStat = CardBodyCompany['mediaplanStats'][number];

const toChartId = (metric: CardMetric): ChartIds => metric as ChartIds;

// Additive vs ratio — это тот же дискриминатор, что и в getPlanPoint:
// наличие secondaryPredicate (кумулятивная линия) ⇒ additive, иначе ratio.
const isAdditiveChart = (chartId: ChartIds): boolean =>
  Boolean(CHARTS_PREDICATES[chartId]?.secondaryPredicate);

// Сырое поле graphStats для аддитивных метрик.
// keep in sync with collectGraphStats.ts (budget←spent, shows←impressions, clicks←clicks).
const graphStatKeyByChartId = (chartId: ChartIds): keyof GraphStat | null => {
  switch (chartId) {
    case 'budget':
      return 'spent';
    case 'clicks':
      return 'clicks';
    case 'shows':
      return 'impressions';
    default:
      return null;
  }
};

// Компонент ComplexValue, соответствующий вспомогательному графику (forecastDataA/B),
// маппится на сырое поле mediaplanStats (паритет с collectMediaplanStats / ComplexValueId).
// keep in sync with collectMediaplanStats.ts.
// ВАЖНО: mediaplanStats не содержит поля clicks → для ratio-компонента 'clicks'
// (cpc.forecastDataB, ctr.forecastDataA) clicks-серия
// сорсится отдельно из graphStats (см. componentSeries / clicksSeriesFromGraphStats).
const componentKeyByChartId = (chartId: ChartIds): keyof MediaplanStat | null => {
  switch (chartId) {
    case 'budget':
      return 'spent';
    case 'shows':
      return 'impressions';
    case 'income':
      return 'revenue';
    case 'reach':
      return 'reach';
    case 'conversions':
      return 'conversions';
    case 'videoComplete':
      return 'videoComplete';
    case 'bounces':
      return 'bounces';
    default:
      // clicks/engagement и пр. — отсутствуют в mediaplanStats или производные.
      return null;
  }
};

// repeat с бэка — это AccountsMediaplanRepeat ('MONTHLY' | 'WEEKLY'), что уже является подмножеством
// PlanPeriodInput.repeat ('MONTHLY' | 'WEEKLY' | null). type 'FLIGHT' не повторяется → repeat null.
// Маппинг Mediaplan → PlanPeriodInput делегирован shared-хелперу toPlanPeriodInput.
type Mediaplan = CardBodyCompany['mediaplans'][number];

const toPlanInputs = (company: CardBodyCompany): PlanPeriodInput[] =>
  company.mediaplans.map(toPlanPeriodInput);

// Активный медиаплан (исходный, с budget/kpis) + сведённый к конкретным датам период.
export interface ActiveMediaplan {
  plan: Mediaplan;
  period: Period;
}

// selectActivePeriod возвращает только PlanPeriodInput (без budget/kpis), поэтому исходный план
// находим тем же предикатом (selectActivePeriod уже выбрал «какой-то» план — здесь нам нужен
// именно тот источник, чтобы прочитать плановое значение).
export const selectActiveMediaplan = (
  company: CardBodyCompany,
  today: IsoDate,
): ActiveMediaplan | null => {
  const active = selectActivePeriod({plans: toPlanInputs(company), today});
  if (!active) {
    return null;
  }

  const plan = company.mediaplans.find(candidate => {
    const input = toPlanPeriodInput(candidate);
    return (
      input.startOn === active.plan.startOn &&
      input.endOn === active.plan.endOn &&
      input.repeat === active.plan.repeat
    );
  });

  if (!plan) {
    return null;
  }

  // period берём из selectActivePeriod (оно уже учло edge-case 1-го числа через effectiveDate).
  return {plan, period: active.period};
};

// Все календарные дни периода [start, end] включительно (UTC).
const enumeratePeriodDays = (period: Period): IsoDate[] => {
  const days: IsoDate[] = [];
  let cursor = new Date(period.start);
  const end = new Date(period.end);
  while (cursor.getTime() <= end.getTime()) {
    days.push(toIso(cursor));
    cursor = addDays(cursor, 1);
  }
  return days;
};

// forecastDays = today+1 .. конец периода.
const buildForecastDays = (period: Period, today: IsoDate): IsoDate[] => {
  const todayObj = new Date(today);
  const end = new Date(period.end);
  const days: IsoDate[] = [];
  let cursor = addDays(todayObj, 1);
  while (cursor.getTime() <= end.getTime()) {
    days.push(toIso(cursor));
    cursor = addDays(cursor, 1);
  }
  return days;
};

// Инвариант calcForecast: каждый день прогноза строго ПОЗЖЕ любого дня со значением (факта).
// На реальных данных номинальный today не всегда совпадает с последним днём факта — факт может
// тянуться до/за today+1, из-за чего наивные forecastDays пересекаются с днями факта и
// calcForecast бросает overlap-ошибку. Отбрасываем дни прогноза <= последнего дня значений.
// Пустой результат = факт покрыл/перекрыл период → прогноза нет (только факт+план).
const dropOverlapWithValues = (forecastDays: IsoDate[], valueDays: IsoDate[]): IsoDate[] => {
  if (valueDays.length === 0) {
    return forecastDays;
  }
  const maxValueDay = [...valueDays].sort()[valueDays.length - 1];
  return forecastDays.filter(day => day > maxValueDay);
};

// Минимум двух ISO-дат (строковое сравнение валидно для yyyy-MM-dd).
const minIso = (a: IsoDate, b: IsoDate): IsoDate => (a <= b ? a : b);

// Fact series from graphStats, bounded to [lowerBound, upperBound]. The body is loaded with a
// wide window (prev/next month) to cover cross-month flights and the 1st-of-month previous period,
// so fact must be clamped to the active period: upperBound = min(today, period.end) keeps the
// future (forecast, not fact) out, lowerBound = period.start keeps a neighbouring period's data
// from leaking in. lowerBound null = no active plan (fact itself defines the period).
const factSeriesFromGraphStats = (
  graphStats: ReadonlyArray<GraphStat>,
  key: keyof GraphStat,
  lowerBound: IsoDate | null,
  upperBound: IsoDate,
): DailySeries =>
  graphStats.reduce<DailySeries>((acc, stat) => {
    const value = stat[key];
    const inRange = stat.date <= upperBound && (lowerBound === null || stat.date >= lowerBound);
    if (typeof value === 'number' && inRange) {
      acc[stat.date] = (acc[stat.date] ?? 0) + value;
    }
    return acc;
  }, {});

// Same bounded daily series, but from mediaplanStats (keyed by collectedOn). Used for additive count
// KPIs whose field isn't in graphStats — conversions (лиды), reach, income, videoComplete.
const factSeriesFromMediaplanStats = (
  mediaplanStats: ReadonlyArray<MediaplanStat>,
  key: keyof MediaplanStat,
  lowerBound: IsoDate | null,
  upperBound: IsoDate,
): DailySeries =>
  mediaplanStats.reduce<DailySeries>((acc, stat) => {
    const value = stat[key];
    const inRange =
      stat.collectedOn <= upperBound && (lowerBound === null || stat.collectedOn >= lowerBound);
    if (typeof value === 'number' && inRange) {
      acc[stat.collectedOn] = (acc[stat.collectedOn] ?? 0) + value;
    }
    return acc;
  }, {});

// План на день = planForPeriod / число дней периода (getPlanForDate, kind=additive),
// раскинутый на каждый день периода — паритет с getPlanPoint по точкам.
const buildPlanDaily = (planForPeriod: number, period: Period): DailySeries => {
  const perDay = getPlanForDate({kind: 'additive', planForPeriod, period});
  return enumeratePeriodDays(period).reduce<DailySeries>((acc, day) => {
    acc[day] = perDay;
    return acc;
  }, {});
};

// Числовое значение плана для chartId из активного плана (бюджет или KPI). null = плана нет.
const readPlanValue = (plan: Mediaplan, chartId: ChartIds): number | null => {
  if (chartId === 'budget') {
    return plan.budget;
  }

  const raw = (plan.kpis ?? []).find(kpi => {
    try {
      return getChartIdByKpiName(kpi.name) === chartId;
    } catch {
      return false;
    }
  })?.value;

  if (raw === undefined || raw === null) {
    return null;
  }

  // parseFloat (not parseInt) — KPI plans like CPL/CPC/ДРР/ROMI are decimals (e.g. «12.5»);
  // parseInt would truncate the fraction.
  const parsed = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
  return isNaN(parsed) ? null : parsed;
};

interface RatioBuildOptions {
  chartId: ChartIds;
  mediaplanStats: ReadonlyArray<MediaplanStat>;
  graphStats: ReadonlyArray<GraphStat>;
}

// Дневная серия clicks из graphStats (date→clicks). mediaplanStats не содержит clicks,
// поэтому ratio-компонент 'clicks' (cpc/ctr/declines forecastData) сорсится отсюда.
const clicksSeriesFromGraphStats = (graphStats: ReadonlyArray<GraphStat>): DailySeries =>
  graphStats.reduce<DailySeries>((acc, stat) => {
    if (typeof stat.clicks === 'number') {
      acc[stat.date] = (acc[stat.date] ?? 0) + stat.clicks;
    }
    return acc;
  }, {});

// Дневная серия одного forecast-компонента (для A/B forecast-серий).
// Компонент 'clicks' читается из graphStats (mediaplanStats его не содержит);
// остальные (spent/conversions/impressions/revenue/reach/...) — из mediaplanStats.
const componentSeries = (
  mediaplanStats: ReadonlyArray<MediaplanStat>,
  graphStats: ReadonlyArray<GraphStat>,
  componentChartId: ChartIds,
): DailySeries => {
  if (componentChartId === 'clicks') {
    return clicksSeriesFromGraphStats(graphStats);
  }

  const key = componentKeyByChartId(componentChartId);
  if (!key) {
    return {};
  }
  return mediaplanStats.reduce<DailySeries>((acc, stat) => {
    const value = stat[key];
    if (typeof value === 'number') {
      acc[stat.collectedOn] = (acc[stat.collectedOn] ?? 0) + value;
    }
    return acc;
  }, {});
};

const buildRatioSpec = ({chartId, mediaplanStats, graphStats}: RatioBuildOptions): RatioSpec => {
  const widget = WIDGETS_LIST[chartId];
  const predicate = CHARTS_PREDICATES[chartId];

  // Мешок компонентов по дням: сырые значения mediaplanStats (паритет с GraphComplexValues.values).
  const bagByDay: Record<IsoDate, ComponentBag> = {};
  const factDays: IsoDate[] = [];

  mediaplanStats.forEach(stat => {
    const {collectedOn} = stat;
    bagByDay[collectedOn] = {
      spent: stat.spent,
      impressions: stat.impressions,
      conversions: stat.conversions,
      revenue: stat.revenue,
      reach: stat.reach,
      bounces: stat.bounces,
      videoComplete: stat.videoComplete,
      engagement: stat.likes + stat.comments + stat.shares,
    };
    factDays.push(collectedOn);
  });

  // complexRatioPredicate типизирован на фиксированный ComplexValue; мешок здесь — ComponentBag
  // (Record<string, number>) с тем же набором ключей. Каст безопасен и зеркалит ratioFactFromChartData
  // в DigitalCast (там aggregateBag(...) as ComplexValue).
  const bagPredicate = (widget.complexRatioPredicate ?? (() => 0)) as (bag: ComponentBag) => number;

  return {
    bagByDay,
    factDays,
    bagPredicate,
    a: componentSeries(
      mediaplanStats,
      graphStats,
      (predicate?.forecastDataA ?? chartId) as ChartIds,
    ),
    b: componentSeries(
      mediaplanStats,
      graphStats,
      (predicate?.forecastDataB ?? chartId) as ChartIds,
    ),
    forecastRange: (predicate?.forecastPredicate ??
      (({rangeA}) => rangeA)) as ComplexForecastPredicate,
  };
};

export const buildMetricInput = ({
  company,
  metric,
  today,
  active: providedActive,
}: Options): MetricInput | null => {
  const chartId = toChartId(metric);
  // Use the pre-resolved active plan when threaded in; otherwise resolve it here.
  const active =
    providedActive !== undefined ? providedActive : selectActiveMediaplan(company, today);

  if (isAdditiveChart(chartId)) {
    // Fact source: graphStats for fields it carries (spent/clicks/impressions); otherwise
    // mediaplanStats for additive counts that live only there (conversions/лиды, reach, income,
    // videoComplete). Unknown on both → nothing to plot.
    const graphKey = graphStatKeyByChartId(chartId);
    const mediaplanKey = graphKey ? null : componentKeyByChartId(chartId);

    // С активным планом факт ограничен периодом [start, min(today, end)]; без плана — просто ≤today
    // (факт сам задаёт период).
    const upperBound = active ? minIso(today, active.period.end) : today;
    const lowerBound = active ? active.period.start : null;

    let factDaily: DailySeries;
    if (graphKey) {
      factDaily = factSeriesFromGraphStats(company.graphStats, graphKey, lowerBound, upperBound);
    } else if (mediaplanKey) {
      factDaily = factSeriesFromMediaplanStats(
        company.mediaplanStats,
        mediaplanKey,
        lowerBound,
        upperBound,
      );
    } else {
      return null;
    }
    if (Object.keys(factDaily).length === 0) {
      return null;
    }

    // Период берём из активного плана; без плана выравниваем по фактическим дням.
    const factDays = Object.keys(factDaily).sort();
    const period: Period = active?.period ?? {
      start: factDays[0],
      end: factDays[factDays.length - 1],
    };

    const planValue = active ? readPlanValue(active.plan, chartId) : null;
    const planDaily = planValue !== null ? buildPlanDaily(planValue, period) : {};

    // Прогноз должен стартовать строго после последнего дня факта (input.factDaily — values).
    const forecastDays = dropOverlapWithValues(
      buildForecastDays(period, today),
      Object.keys(factDaily),
    );

    return {
      kind: 'additive',
      planDaily,
      factDaily,
      today,
      forecastDays,
    };
  }

  // Ratio-метрики требуют активного плана.
  if (!active) {
    return null;
  }

  const planForPeriod = readPlanValue(active.plan, chartId);
  if (planForPeriod === null) {
    return null;
  }

  // Bound ratio fact to the active period [start, min(today, end)], symmetrically with the additive
  // path. upperBound keeps the future out (forecast, not fact) so CPL/CPC/ДРР/CTR don't plot/aggregate
  // days that haven't happened and the forecast regression isn't poisoned by future zeros; lowerBound
  // keeps a neighbouring period's data (the body window spans prev/next month) from leaking in.
  const upperBound = minIso(today, active.period.end);
  const lowerBound = active.period.start;
  const inPeriod = (day: IsoDate) => day >= lowerBound && day <= upperBound;
  const ratio = buildRatioSpec({
    chartId,
    mediaplanStats: company.mediaplanStats.filter(stat => inPeriod(stat.collectedOn)),
    graphStats: company.graphStats.filter(stat => inPeriod(stat.date)),
  });

  // Прогноз ratio считается по сериям a и b (calcForecast для forecastA/forecastB).
  // Стартовать он должен строго после последнего дня, присутствующего в a∪b
  // (реальный последний день компонент — может быть из mediaplanStats.collectedOn или graphStats clicks).
  const forecastDays = dropOverlapWithValues(buildForecastDays(active.period, today), [
    ...Object.keys(ratio.a),
    ...Object.keys(ratio.b),
  ]);

  // planToDate = план, пропорциональный числу дней до today (для ratio план постоянен по дню,
  // но движок ожидает скаляры; для ratio planToDate == planForPeriod — план не накапливается).
  return {
    kind: 'ratio',
    planForPeriod,
    planToDate: planForPeriod,
    ratio,
    forecastDays,
  };
};
